import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

// ── Pricing Settings ──────────────────────────────────────────────
// Lets Dr. Vijay edit the tiered flat-fee pricing shown to new
// societies in SetupWizard.jsx's "Pricing & Terms" step, without
// needing a code change. Everything lives in one Firestore doc,
// config/pricing, so SetupWizard.jsx only needs one read to get the
// current tiers. See Adyantam_Pricing_Decision_Notes, Section 7.3,
// for the reasoning behind the numbers themselves.

const DOC_PATH = ["config", "pricing"];

function emptyTier() {
  return { minFlats: "", maxFlats: "", priceMonthly: "", label: "" };
}

const S = {
  page: { padding: "24px", maxWidth: "760px", margin: "0 auto", color: "#e8e8f0" },
  title: { fontSize: "22px", fontWeight: "700", marginBottom: "4px" },
  sub: { fontSize: "13px", color: "#8888a8", marginBottom: "24px" },
  card: { background: "#1b1d27", border: "1px solid #2e2e40", borderRadius: "12px", padding: "18px", marginBottom: "16px" },
  row: { display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center" },
  label: { fontSize: "12px", color: "#8888a8", display: "block", marginBottom: "4px" },
  input: { width: "100%", padding: "9px 10px", borderRadius: "8px", border: "1px solid #2e2e40", background: "#111217", color: "#fff", fontSize: "14px", boxSizing: "border-box" },
  field: { flex: 1 },
  removeBtn: { background: "none", border: "none", color: "#ff6b6b", cursor: "pointer", fontSize: "18px", padding: "0 6px", marginTop: "18px" },
  addBtn: { padding: "10px 16px", borderRadius: "8px", border: "1px dashed #6c63ff", background: "transparent", color: "#a29bff", cursor: "pointer", fontSize: "13px", fontWeight: "600" },
  saveBtn: { padding: "12px 20px", borderRadius: "8px", border: "none", background: "#6c63ff", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: "700", marginTop: "10px" },
  status: { fontSize: "13px", marginTop: "10px" },
};

export default function PricingSettingsScreen() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const isSalesHead = user?.role === "SalesHead";
  const [tiers, setTiers] = useState([emptyTier()]);
  const [customAboveFlats, setCustomAboveFlats] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, ...DOC_PATH));
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data.tiers) && data.tiers.length > 0) setTiers(data.tiers);
          setCustomAboveFlats(data.customAboveFlats ?? "");
          setCustomMessage(data.customMessage ?? "");
        }
      } catch (err) {
        console.error("Pricing load failed:", err);
        setStatus("Couldn't load current pricing — starting from blank. " + (err.message || ""));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function updateTier(index, key, value) {
    setTiers(prev => prev.map((t, i) => (i === index ? { ...t, [key]: value } : t)));
  }

  function addTier() {
    setTiers(prev => [...prev, emptyTier()]);
  }

  function removeTier(index) {
    setTiers(prev => prev.filter((_, i) => i !== index));
  }

  function validate() {
    if (tiers.length === 0) return "Add at least one pricing tier.";
    for (const t of tiers) {
      if (!t.minFlats || !t.maxFlats || !t.priceMonthly)
        return "Every tier needs Min Flats, Max Flats, and Price/Month filled in.";
      if (Number(t.minFlats) > Number(t.maxFlats))
        return "A tier's Min Flats can't be greater than its Max Flats.";
    }
    return "";
  }

  async function handleSave() {
    const err = validate();
    if (err) { setStatus("⚠ " + err); return; }
    setSaving(true);
    setStatus("");
    try {
      const cleanTiers = tiers.map(t => ({
        minFlats: Number(t.minFlats),
        maxFlats: Number(t.maxFlats),
        priceMonthly: Number(t.priceMonthly),
        label: t.label.trim() || `${t.minFlats}-${t.maxFlats} flats`,
      }));
      await setDoc(doc(db, ...DOC_PATH), {
        tiers: cleanTiers,
        customAboveFlats: customAboveFlats ? Number(customAboveFlats) : null,
        customMessage: customMessage.trim(),
        updatedAt: new Date().toISOString(),
      });
      setStatus("✓ Saved. New societies in Setup Wizard will see these prices immediately.");
    } catch (err) {
      console.error("Pricing save failed:", err);
      setStatus("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  if (!isOwner && !isSalesHead) {
    return (
      <div style={S.page}>
        <div style={S.card}>You do not have permission to view Pricing Settings.</div>
      </div>
    );
  }

  if (loading) {
    return <div style={S.page}>Loading current pricing…</div>;
  }

  return (
    <div style={S.page}>
      <div style={S.title}>💰 Pricing Settings</div>
      <div style={S.sub}>These prices are shown to every new society in Setup Wizard's "Pricing & Terms" step, based on their flat count. Changes here apply immediately — no code change needed.</div>

      {tiers.map((t, i) => (
        <div key={i} style={S.card}>
          <div style={S.row}>
            <div style={S.field}>
              <label style={S.label}>Min Flats</label>
              <input style={S.input} type="number" min="1" value={t.minFlats}
                onChange={e => updateTier(i, "minFlats", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Max Flats</label>
              <input style={S.input} type="number" min="1" value={t.maxFlats}
                onChange={e => updateTier(i, "maxFlats", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Price / Month (₹)</label>
              <input style={S.input} type="number" min="0" value={t.priceMonthly}
                onChange={e => updateTier(i, "priceMonthly", e.target.value)} />
            </div>
            <button style={S.removeBtn} onClick={() => removeTier(i)} title="Remove this tier">✕</button>
          </div>
          <div>
            <label style={S.label}>Label shown to customer (optional — auto-filled if left blank)</label>
            <input style={S.input} placeholder={`e.g. Up to ${t.maxFlats || "15"} flats`}
              value={t.label} onChange={e => updateTier(i, "label", e.target.value)} />
          </div>
        </div>
      ))}

      <button style={S.addBtn} onClick={addTier}>+ Add another tier</button>

      <div style={{ ...S.card, marginTop: "20px" }}>
        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Above this many flats, show a custom message instead of a price</label>
            <input style={S.input} type="number" min="1" placeholder="e.g. 50"
              value={customAboveFlats} onChange={e => setCustomAboveFlats(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={S.label}>Custom message</label>
          <input style={S.input} placeholder="e.g. Let's talk — contact us directly for larger societies."
            value={customMessage} onChange={e => setCustomMessage(e.target.value)} />
        </div>
      </div>

      <button style={{ ...S.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
        onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Save Pricing"}
      </button>

      {status && (
        <div style={{ ...S.status, color: status.startsWith("✓") ? "#4caf50" : "#ff6b6b" }}>{status}</div>
      )}
    </div>
  );
}
